import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getMergedAttributeTypeFacets, getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import clearAllProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/ClearAllProductFilters";
import removeProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/RemoveProductFilters";
import setFilterQuery from "@insite/client-framework/Store/Pages/ProductList/Handlers/SetFilterQuery";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import {
    AttributeTypeFacetModel,
    CategoryFacetModel,
    FacetModel,
    PriceFacetModel,
    ProductModel,
} from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { ProductListPageDataContext } from "@insite/content-library/Pages/ProductListPage";
import { formatPriceRangeFacet } from "@insite/content-library/Widgets/ProductList/ProductListPriceFilters";
import Button from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Icon from "@insite/mobius/Icon";
import Filter from "@insite/mobius/Icons/Filter";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Tag, { TagPresentationProps } from "@insite/mobius/Tag";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import hideAndShowFilterHandler from "@tSurya/Handlers/HideAndShowFilterHandler";
import { CustomProductFilters } from "@tSurya/Handlers/ProductList/AddProductAvailibility";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => {
    const {
        pages: {
            productList: { productFilters, unfilteredApiParameter, filteredApiParameter },
        },
        context: {
            session: { currency, language },
        },
    } = state;
    const ProductFiltersUpdated: CustomProductFilters = state.pages.productList.productFilters;
    if (getProductListDataView(state).value) {
        const unfilteredDataView = getProductsDataView(state, unfilteredApiParameter);
        const unfilteredProductCollection = unfilteredDataView.value ? unfilteredDataView : undefined;
        const filteredDataView = getProductsDataView(state, filteredApiParameter);
        const filteredProductCollection = filteredDataView.value ? filteredDataView : undefined;

        return {
            loaded: true,
            stockedItemsOnly: productFilters.stockedItemsOnly ?? false,
            previouslyPurchasedProducts: productFilters.previouslyPurchasedProducts ?? false,
            brandFacets: unfilteredProductCollection?.brandFacets || filteredProductCollection?.brandFacets,
            productLineFacets:
                unfilteredProductCollection?.productLineFacets || filteredProductCollection?.productLineFacets,
            categoryFacets: unfilteredProductCollection?.categoryFacets,
            priceFacets:
                unfilteredProductCollection?.priceRange?.priceFacets ||
                filteredProductCollection?.priceRange?.priceFacets,
            attributeTypeFacets: getMergedAttributeTypeFacets(state,
                unfilteredApiParameter,
                filteredApiParameter,
            ),
            searchWithinQueries: productFilters.searchWithinQueries,
            selectedBrandIds: productFilters.brandIds,
            selectedProductLineIds: productFilters.productLineIds,
            selectedPriceFilters: productFilters.priceFilters,
            selectedAttributeValuesIds: productFilters.attributeValueIds,
            selectedCategoryId: productFilters.categoryId,
            currency,
            language,
            minimumPrice: ProductFiltersUpdated.minimumPrice,
            productsDataView: getProductListDataView(state).value,
        };
    }
    return {
        loaded: false,
        stockedItemsOnly: false,
        previouslyPurchasedProducts: false,
        brandFacets: [] as FacetModel[],
        productLineFacets: [] as FacetModel[],
        categoryFacets: [] as CategoryFacetModel[],
        priceFacets: [] as PriceFacetModel[],
        attributeTypeFacets: [] as AttributeTypeFacetModel[],
        searchWithinQueries: [] as string[],
        selectedBrandIds: [] as string[],
        selectedProductLineIds: [] as string[],
        selectedPriceFilters: [] as string[],
        selectedAttributeValuesIds: [] as string[],
        selectedCategoryId: "",
        currency: null,
        language: null,
        minimumPrice: "",
        productsDataView: [] as ProductModel[] | undefined,
    };
};

const mapDispatchToProps = {
    removeProductFilters,
    clearAllProductFilters,
    hideAndShowFilterHandler,
    setFilterQuery,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface ProductListFilterListStyles {
    wrapper?: InjectableCss;
    skipFiltersButtonWrapper?: InjectableCss;
    skipFiltersButton?: SkipNavStyles;
    filterText?: TypographyPresentationProps;
    filterTag?: TagPresentationProps;
    clearAllLink?: LinkPresentationProps;
    hideFilterBtnStyles?: InjectableCss;
    desktopFilterStyles?: HiddenProps;
    iconFilterStyles?: InjectableCss;
    selectedItemsContainer?: InjectableCss;
    filterButtonStyles?: InjectableCss;
}

export const filterListStyles: ProductListFilterListStyles = {
    filterButtonStyles: {
        css: css`
            display: flex;
            justify-content: space-between;
            width: 100%;
            flex-wrap: wrap;
            gap: 10px;
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            margin-top: 15px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 9px;
                    `,
                    css`
                        margin-top: 9px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    selectedItemsContainer: {
        css: css`
            display: inline-flex;
            flex-wrap: wrap;
        `,
    },
    filterTag: {
        css: css`
            background: ${getColor("common.backgroundContrast")};
            min-height: none !important;
            color: ${getColor("text.accent")};
            margin-top: 10px;
            padding-top: 8px;
            padding-bottom: 8px;
            padding-right: 8px;
            border-radius: 0;
            align-items: center;
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-style: normal;
            font-weight: ${TYPOGRAPHY.weight.base};
            font-size: ${TYPOGRAPHY.size.small};
            line-height: 24px;
            border: 1px solid ${getColor("common.backgroundContrast")};
            > span:first-of-type {
                margin-top: 0px;
                margin-bottom: 0px;
                color: ${getColor("text.accent")};
            }
            > button {
                min-height: auto !important;
                > span:last-of-type {
                    color: ${getColor("text.accent")};
                    background: ${getColor("common.backgroundContrast")} !important;
                    > svg {
                        height: 20px !important;
                        width: 20px !important;
                    }
                }
            }
        `,
    },
    filterText: {
        variant: "h3",
    },
    skipFiltersButtonWrapper: {
        css: css`
            position: relative;
            top: -65px;
            width: 100%;
        `,
    },
    clearAllLink: {
        css: css`
            padding-bottom: 0px;
            > span {
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-style: normal;
                font-weight: ${TYPOGRAPHY.weight.bold};
                font-size: ${TYPOGRAPHY.size.small};
                line-height: 24px;
                color: ${getColor("text.accent")} !important;
            }
        `,
    },
    hideFilterBtnStyles: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            background-color: ${getColor("secondary")};
            color: ${getColor("text.main")};
            font-family: ${TYPOGRAPHY.fontFamily.title};
            border: 2px solid ${getColor("text.main")};
            border-radius: 1px;
            font-weight: 700;
            font-size: 16px;
            line-height: 24px;
            &:hover {
                background-color: ${getColor("secondary")};
                color: ${getColor("text.main")};
            }
            > span:last-of-type {
                padding: 9px 0;
            }
        `,
    },
    desktopFilterStyles: {
        css: css`
            width: 100%;
        `,
    },
    iconFilterStyles: {
        css: css`
            height: 18px;
            width: 19px;
            margin-right: 10px;
        `,
    },
};

const styles = filterListStyles;

const ProductListFilterList: FC<Props> = ({
    loaded,
    stockedItemsOnly,
    previouslyPurchasedProducts,
    brandFacets,
    productLineFacets,
    priceFacets,
    categoryFacets,
    attributeTypeFacets,
    searchWithinQueries,
    selectedBrandIds,
    selectedProductLineIds,
    selectedPriceFilters,
    selectedAttributeValuesIds,
    selectedCategoryId,
    removeProductFilters,
    clearAllProductFilters,
    currency,
    language,
    hideAndShowFilterHandler,
    minimumPrice,
    setFilterQuery,
    productsDataView,
}) => {
    if (!loaded) {
        return null;
    }

    const clickRemoveStockedItemsOnly = () => {
        removeProductFilters({ stockedItemsOnly: false });
    };

    const removeProductAvailbility = () => {
        removeProductFilters({
            minimumPrice,
        } as CustomProductFilters);
    };

    const clickRemovePreviouslyPurchasedProducts = () => {
        removeProductFilters({ previouslyPurchasedProducts: false });
    };

    const clickRemoveSearchHandler = (search: string) => {
        removeProductFilters({ searchWithinQueries: [search] });
    };

    const clickRemoveBrandHandler = (id: string) => {
        removeProductFilters({ brandIds: [id] });
    };

    const clickRemoveProductLineHandler = (id: string) => {
        removeProductFilters({ productLineIds: [id] });
    };

    const clickRemovePriceHandler = (price: string) => {
        removeProductFilters({ priceFilters: [price] });
    };

    const clickRemoveAttributeValueHandler = (id: string) => {
        removeProductFilters({ attributeValueIds: [id] });
    };

    const clickRemoveCategoryHandler = () => {
        removeProductFilters({ categoryId: selectedCategoryId });
    };

    const anyActiveFilters =
        searchWithinQueries?.length ||
        stockedItemsOnly ||
        minimumPrice ||
        previouslyPurchasedProducts ||
        (brandFacets && selectedBrandIds?.length) ||
        (productLineFacets && selectedProductLineIds?.length) ||
        (priceFacets && selectedPriceFilters?.length) ||
        (attributeTypeFacets && selectedAttributeValuesIds?.length) ||
        selectedCategoryId;

    const [toggle, SetToggle] = useState(false);

    const onClick = () => {
        SetToggle(!toggle);
        hideAndShowFilterHandler({ toggle: !toggle });
    };

    const applyFilterHandler = () => {
        setFilterQuery();
    };

    return (
        <>
            {/* {anyActiveFilters && (
                <StyledWrapper {...styles.filterButtonStyles}>
                    <Button onClick={applyFilterHandler} css={css`width:100%;`}>{translate("Apply filter")}</Button>
                    <Button variant="secondary" onClick={() => clearAllProductFilters()} css={css`width:100%;`}>
                        {translate("Clear All")}
                    </Button>
                </StyledWrapper>
            )} */}

            <StyledWrapper {...styles.wrapper}>
                {/* <Hidden below="lg" {...styles.desktopFilterStyles}>
                    <Button {...styles.hideFilterBtnStyles} onClick={onClick}>
                        <Icon src={Filter} {...styles.iconFilterStyles} />
                        {toggle === true ? <span>Show Filters</span> : <span>Hide Filters</span>}
                    </Button>
                </Hidden> */}

                <StyledWrapper {...styles.skipFiltersButtonWrapper}>
                    <ProductListPageDataContext.Consumer>
                        {({ ref }) => {
                            return ref ? (
                                <SkipNav
                                    text={translate("Skip to Results")}
                                    extendedStyles={styles.skipFiltersButton}
                                    destination={ref}
                                />
                            ) : undefined;
                        }}
                    </ProductListPageDataContext.Consumer>
                </StyledWrapper>
                {/* {minimumPrice && (
                    <Tag
                        {...styles.filterTag}
                        onDelete={removeProductAvailbility}
                        data-test-selector="previouslyPurchasedProductsFilter"
                    >
                        {minimumPrice.split(".")[0] === "0" && minimumPrice.split(".")[1] === "90"
                            ? `Available Over 60 Days`
                            : minimumPrice.split(".")[0] === "0" && minimumPrice.split(".")[1] === "60"
                                ? `Available Within 60 Days`
                                : minimumPrice.split(".")[0] === "0" && minimumPrice.split(".")[1] === "30"
                                    ? `Available Within 30 Days`
                                    : minimumPrice.split(".")[0] === "0" && minimumPrice.split(".")[1] === "00"
                                        ? `Available Today`
                                        : minimumPrice.split(".")[0] !== "0" && minimumPrice.split(".")[1] === "90"
                                            ? `Quantity: ${minimumPrice.split(".")[0]} Available Over 60 Days`
                                            : minimumPrice.split(".")[0] !== "0" && minimumPrice.split(".")[1] === "60"
                                                ? `Quantity: ${minimumPrice.split(".")[0]} Available Within 60 Days`
                                                : minimumPrice.split(".")[0] !== "0" && minimumPrice.split(".")[1] === "30"
                                                    ? `Quantity: ${minimumPrice.split(".")[0]} Available Within 30 Days`
                                                    : minimumPrice.split(".")[0] !== "0" && minimumPrice.split(".")[1] === "00"
                                                        ? `Quantity: ${minimumPrice.split(".")[0]} Available Today`
                                                        : null}
                    </Tag>
                )}
                {previouslyPurchasedProducts && (
                    <Tag
                        {...styles.filterTag}
                        onDelete={clickRemovePreviouslyPurchasedProducts}
                        data-test-selector="previouslyPurchasedProductsFilter"
                    >
                        {translate("Previously Purchased Only")}
                    </Tag>
                )}
                {stockedItemsOnly && (
                    <Tag
                        {...styles.filterTag}
                        onDelete={clickRemoveStockedItemsOnly}
                        data-test-selector="stockedItemsOnlyFilter"
                    >
                        {translate("Stocked Items Only")}
                    </Tag>
                )}
                {searchWithinQueries?.map(s => (
                    <Tag
                        key={s}
                        {...styles.filterTag}
                        onDelete={() => clickRemoveSearchHandler(s)}
                        data-test-selector="searchWithinFilter"
                    >
                        {translate("Search")}: {s}
                    </Tag>
                ))}
                {productsDataView && brandFacets &&
                    selectedBrandIds?.map(id => (<>
                        {brandFacets?.find(o => o.id === id)?.name ? <Tag

                            key={id}
                            {...styles.filterTag}
                            onDelete={() => clickRemoveBrandHandler(id)}
                            data-test-selector={`brandFilter${id}`}
                        >{brandFacets?.find(o => o.id === id)?.name}</Tag> : ""}
                    </>
                    ))}
                {productLineFacets &&
                    selectedProductLineIds?.map(id => (<>
                        {productLineFacets?.find(o => o.id === id)?.name ? <Tag

                            key={id}
                            {...styles.filterTag}
                            onDelete={() => clickRemoveProductLineHandler(id)}
                            data-test-selector={`productLineFilter${id}`}
                        >
                            {productLineFacets?.find(o => o.id === id)?.name}
                        </Tag> : ""}

                    </>

                    ))}
                {productsDataView && selectedCategoryId && (<>
                    {categoryFacets?.find(o => o.categoryId === selectedCategoryId)?.shortDescription ? <Tag

                        {...styles.filterTag}
                        onDelete={() => clickRemoveCategoryHandler()}
                        data-test-selector={`categoryFilter${selectedCategoryId}`}
                    >
                        {categoryFacets?.find(o => o.categoryId === selectedCategoryId)?.shortDescription}
                    </Tag> : ""}
                </>


                )}
                {priceFacets &&
                    selectedPriceFilters?.map(id => (
                        <Tag
                            key={id}
                            {...styles.filterTag}
                            onDelete={() => clickRemovePriceHandler(id)}
                            data-test-selector={`priceFilter${id}`}
                        >
                            {formatPriceRangeFacet(
                                priceFacets?.find(o => o.minimumPrice.toString() === id),
                                currency,
                                language,
                            )}
                        </Tag>
                    ))} */}
                <StyledWrapper {...styles.selectedItemsContainer}>
                    {/* {attributeTypeFacets &&
                        selectedAttributeValuesIds?.map(id => {
                            const attributeTypeFacet = attributeTypeFacets?.find(at =>
                                at.attributeValueFacets?.find(av => av.attributeValueId === id),
                            );
                            const attributeValueFacet = attributeTypeFacet?.attributeValueFacets?.find(
                                av => av.attributeValueId === id,
                            );


                            return (
                                // <Hidden below="md">
                                <>{attributeValueFacet &&(<Tag
                                    key={id}
                                    {...styles.filterTag}
                                    onDelete={() => clickRemoveAttributeValueHandler(id)}
                                    data-test-selector={`attributeValueFilter${id}`}
                                >
                                    {attributeValueFacet?.valueDisplay.includes("|")
                                        ? `${attributeValueFacet?.valueDisplay.split("|")[0]} (${attributeValueFacet?.valueDisplay.split("|")[1]
                                        })`
                                        : attributeValueFacet?.valueDisplay}
                                </Tag>)}</>
                                // </Hidden>
                            );
                        })} */}
                    {anyActiveFilters && (
                        <Link
                            {...styles.clearAllLink}
                            onClick={() => clearAllProductFilters()}
                            data-test-selector="productListClearAll"
                        >
                            {translate("Clear All")}
                        </Link>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListFilterList),
    definition: {
        group: "Product List",
        displayName: "Filter List",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
